import { isUnlimited } from '@qr/shared/plans';
import { Callout, Spinner } from '@qr/ui';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { OrgSwitcher } from '../components/OrgSwitcher.js';
import { SelectorDeTema } from '../components/SelectorDeTema.js';
import { puedeVerCobros } from '../lib/cobros.js';
import { useAuth } from '../lib/auth.js';
import { useOrg } from '../lib/org.js';

/**
 * Armazon del panel: navegacion, estado de la cuenta y guardia de acceso.
 *
 * La navegacion va en una barra lateral y no arriba por una razon de
 * crecimiento: en horizontal cada seccion nueva compite por un ancho que no
 * da mas de si, mientras que en vertical caben carpetas, campanas o plantillas
 * sin rehacer nada. En movil no cabe, asi que ahi se repliega a un cajon.
 *
 * Es tambien el sitio donde se decide que ve una cuenta de cortesia: ahi no se
 * muestran ni medidor de consumo ni invitaciones a mejorar el plan. A un
 * cliente al que se le ha regalado el servicio no se le ensena cuanto "gasta".
 */
export function Layout() {
  const { user, loading: authLoading, logout, isSuperadmin } = useAuth();
  const org = useOrg();
  const location = useLocation();
  const [cajonAbierto, setCajonAbierto] = useState(false);

  // Al cambiar de seccion el cajon se cierra solo. Sin esto, en movil te
  // quedas mirando el menu encima de la pantalla a la que acabas de ir.
  //
  // Se ajusta durante el render y no en un efecto, como el resto del panel:
  // con un efecto, React llegaria a pintar una vez el menu abierto sobre la
  // pantalla nueva antes de cerrarlo, y se ve el parpadeo.
  const [rutaVista, setRutaVista] = useState(location.pathname);
  if (location.pathname !== rutaVista) {
    setRutaVista(location.pathname);
    setCajonAbierto(false);
  }

  if (authLoading) {
    return (
      <div className="boot">
        <Spinner label="Cargando tu cuenta" />
      </div>
    );
  }

  if (!user) {
    /*
     * Se recuerda a donde iba para volver ahi despues de entrar.
     *
     * Antes esto viajaba en el estado de React y nadie lo leia al otro lado,
     * de modo que quien elegia un plan en la portada acababa en "Codigos" sin
     * que nadie volviera a mencionarlo. Ahora va en la URL, que es lo unico
     * que sobrevive al enlace magico: ahi el usuario sale del navegador, abre
     * su correo y puede volver por otra pestana.
     *
     * Se conserva la query ademas de la ruta, porque es donde viajan el plan y
     * el ciclo elegidos.
     */
    const queria = location.pathname + location.search;
    return <Navigate to={`/acceso?destino=${encodeURIComponent(queria)}`} replace />;
  }

  if (org.loading) {
    return (
      <div className="boot">
        <Spinner label="Cargando tu organización" />
      </div>
    );
  }

  const scanLimit = org.limits.scansPerMonth;
  const showMeter = !org.isComped && isUnlimited(scanLimit) === false;
  const usedRatio = showMeter ? Math.min(org.scansThisMonth / scanLimit, 1) : 0;
  const nearLimit = showMeter && usedRatio >= 0.8;

  const lateral = (
    <div className="flex h-full flex-col gap-5 p-3">
      {/*
        El logotipo, encima del selector de espacio y separado por una linea.

        Son dos identidades distintas y conviene que no se confundan: arriba
        de quien es la herramienta, debajo en que cuenta estas trabajando.
        Juntas sin separacion parecia que "Tekton" fuera el nombre del
        producto.
      */}
      <a
        href="https://apuntaqr.com"
        className="flex justify-center px-2 pt-1.5 pb-4 text-ink no-underline"
        aria-label="Apunta QR"
      >
        <span className="logotipo w-[112px]" role="img" aria-label="Apunta QR" />
      </a>

      <div className="-mt-4 border-t border-line pt-4">
        <OrgSwitcher />
      </div>

      <nav className="flex flex-col gap-0.5" aria-label="Secciones">
        <Seccion to="/" end icono={<IconoCodigos />}>
          Códigos
        </Seccion>
        <Seccion to="/equipo" icono={<IconoEquipo />}>
          Equipo
        </Seccion>
        {/*
          Facturacion solo para quien es propietario.

          A un editor invitado no se le ensena una seccion que, al abrirla, le
          dice que no es para el: el enlace que no lleva a ninguna parte se
          lee como un producto a medias. Y a las cuentas de cortesia tampoco,
          que ahi no hay nada que cobrar.
        */}
        {/*
          Mientras el cobro no este abierto, la seccion solo la ve un
          superadmin: asi se puede probar el circuito entero en produccion sin
          ensenarle a nadie un boton que lleva a una pasarela de pruebas.
        */}
        {org.role === 'owner' && !org.isComped && puedeVerCobros(user.email, isSuperadmin) ? (
          <Seccion to="/facturacion" icono={<IconoFacturacion />}>
            Facturación
          </Seccion>
        ) : null}
        {isSuperadmin ? (
          <Seccion to="/superadmin" icono={<IconoAjustes />}>
            Administración
          </Seccion>
        ) : null}
      </nav>

      <div className="mt-auto flex flex-col gap-2 px-2">
        {showMeter ? (
          <div className="flex flex-col gap-1.5 pb-1">
            <div className="h-1 overflow-hidden rounded-full bg-raise">
              <div
                // En lima mientras va sobrado y en ámbar cuando aprieta: el
                // cambio de color es el aviso, no un adorno.
                className={`h-full rounded-full ${nearLimit ? 'bg-warn' : 'bg-accent'}`}
                style={{ inlineSize: `${usedRatio * 100}%` }}
              />
            </div>
            <span className="text-[11.5px] text-ink-3 tabular-nums">
              {org.scansThisMonth.toLocaleString('es')} de{' '}
              {scanLimit.toLocaleString('es')} escaneos este mes
            </span>
          </div>
        ) : null}

        <SelectorDeTema />

        <span className="truncate text-[11.5px] text-ink-3" title={user.email ?? ''}>
          {user.email}
        </span>
        <button
          type="button"
          className="self-start rounded-sm text-[12.5px] text-ink-2 underline-offset-2 hover:text-ink hover:underline"
          onClick={() => void logout()}
        >
          Salir
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-ground lg:grid lg:grid-cols-[224px_1fr]">
      {/* Barra lateral fija en pantallas grandes. */}
      <aside className="sticky top-0 hidden h-dvh border-r border-line bg-surface lg:block">
        {lateral}
      </aside>

      {/* En movil, una barra superior que abre el cajon. */}
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-line bg-surface px-3 py-2.5 lg:hidden">
        <button
          type="button"
          className="grid size-9 place-items-center rounded-sm border border-line text-ink-2"
          aria-label="Abrir el menú"
          aria-expanded={cajonAbierto}
          onClick={() => setCajonAbierto(true)}
        >
          <svg width="17" height="17" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M2 4h12M2 8h12M2 12h12"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
        {/* En movil el logotipo sustituye al rotulo: ocupa lo mismo y dice
            de quien es la herramienta. */}
        <span className="logotipo w-[96px] text-ink" role="img" aria-label="Apunta QR" />
      </header>

      <AnimatePresence>
        {cajonAbierto ? (
          <>
            <motion.div
              className="fixed inset-0 z-30 bg-black/45 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              onClick={() => setCajonAbierto(false)}
            />
            <motion.aside
              className="fixed inset-y-0 left-0 z-40 w-64 border-r border-line bg-surface lg:hidden"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 420, damping: 38 }}
            >
              {lateral}
            </motion.aside>
          </>
        ) : null}
      </AnimatePresence>

      <main className="min-w-0">
        {org.status === 'suspended' || nearLimit ? (
          <div className="flex flex-col gap-3 px-4 pt-4 lg:px-7">
            {org.status === 'suspended' ? (
              <Callout tone="danger" title="Cuenta suspendida">
                Tus códigos no están redirigiendo ahora mismo. Escríbenos para
                revisarlo.
              </Callout>
            ) : null}

            {nearLimit ? (
              <Callout tone="warning" title="Te acercas al límite de escaneos">
                Llevas {org.scansThisMonth.toLocaleString('es')} de{' '}
                {scanLimit.toLocaleString('es')} escaneos este mes. Al superarlo, tus
                códigos dejarán de redirigir hasta el mes que viene.
              </Callout>
            ) : null}
          </div>
        ) : null}

        <Outlet />
      </main>
    </div>
  );
}

function Seccion({
  to,
  end = false,
  icono,
  children,
}: {
  readonly to: string;
  readonly end?: boolean;
  readonly icono: React.ReactNode;
  readonly children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          'flex items-center gap-2.5 rounded-sm px-2.5 py-2 text-[13.5px] transition-colors',
          // La seccion en la que estas se pinta con la marca y con una
          // barra a la izquierda: el relleno suelto se confundia con el
          // hover, que es gris y esta a un pixel de distancia.
          isActive
            // La barra de la seccion activa va en lima: es el color de la marca
            // que mas dice, y aqui marca algo de verdad —donde estas— en vez de
            // decorar. Aparece una sola vez por pantalla, que es lo que hace que
            // signifique algo.
            ? 'bg-brand-soft font-semibold text-brand-deep shadow-[inset_3px_0_0_0_var(--color-accent)]'
            : 'text-ink-2 hover:bg-raise hover:text-ink',
        ].join(' ')
      }
    >
      <span className="shrink-0" aria-hidden="true">
        {icono}
      </span>
      {children}
    </NavLink>
  );
}

function IconoCodigos() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <rect x="10" y="10" width="4" height="4" fill="currentColor" />
    </svg>
  );
}

function IconoEquipo() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="6" cy="5.5" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M1.6 14c0-2.6 2-4.2 4.4-4.2s4.4 1.6 4.4 4.2"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <circle cx="12.2" cy="6.4" r="1.9" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function IconoAjustes() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 1.4v2M8 12.6v2M1.4 8h2M12.6 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M12.6 3.4l-1.4 1.4M4.8 11.2l-1.4 1.4"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}

/** Una tarjeta: es lo que la gente reconoce como "pagos" sin leer el rotulo. */
function IconoFacturacion() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1" y="3.2" width="14" height="9.6" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1 6.6h14" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3.6 10.2h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
